import { Suspense, lazy, type ComponentProps } from "react";
import { Navigate, Route, Routes, useParams } from "react-router";
import { AppLayout } from "./layout/AppLayout";
import { homeLandingPath, isRouteGroupVisible } from "./navigation";
import { useOrgRendering } from "./useOrgRendering";
import { DashboardPage } from "@/pages/DashboardPage";
import { Spinner } from "@bastion/ui";

const OAuthConsentPage = lazy(() =>
  import("@/pages/OAuthConsentPage").then((module) => ({ default: module.OAuthConsentPage })),
);
const ChatPage = lazy(() =>
  import("@/pages/ChatPage").then((module) => ({ default: module.ChatPage })),
);
const EpisodesPage = lazy(() =>
  import("@/pages/EpisodesPage").then((module) => ({ default: module.EpisodesPage })),
);
const EpisodeDetailPage = lazy(() =>
  import("@/pages/EpisodeDetailPage").then((module) => ({ default: module.EpisodeDetailPage })),
);
const ReviewPage = lazy(() =>
  import("@/pages/ReviewPage").then((module) => ({ default: module.ReviewPage })),
);
const SubtasksPage = lazy(() =>
  import("@/pages/SubtasksPage").then((module) => ({ default: module.SubtasksPage })),
);
const DatasetsPage = lazy(() =>
  import("@/pages/DatasetsPage").then((module) => ({ default: module.DatasetsPage })),
);
const DatasetDetailPage = lazy(() =>
  import("@/pages/DatasetDetailPage").then((module) => ({ default: module.DatasetDetailPage })),
);
const DatasetEpisodeDetailPage = lazy(() =>
  import("@/pages/DatasetEpisodeDetailPage").then((module) => ({
    default: module.DatasetEpisodeDetailPage,
  })),
);
const DatasetCuratePage = lazy(() =>
  import("@/pages/DatasetCuratePage").then((module) => ({ default: module.DatasetCuratePage })),
);
const DatasetBatchLabelPage = lazy(() =>
  import("@/pages/DatasetBatchLabelPage").then((module) => ({
    default: module.DatasetBatchLabelPage,
  })),
);
const MLStudioWorkflowsPage = lazy(() =>
  import("@/pages/MLStudioWorkflowsPage").then((module) => ({
    default: module.MLStudioWorkflowsPage,
  })),
);
const MLStudioWorkflowDetailPage = lazy(() =>
  import("@/pages/MLStudioWorkflowDetailPage").then((module) => ({
    default: module.MLStudioWorkflowDetailPage,
  })),
);
const MLStudioNewWorkflowPage = lazy(() =>
  import("@/pages/MLStudioNewWorkflowPage").then((module) => ({
    default: module.MLStudioNewWorkflowPage,
  })),
);
const MLStudioArtifactsPage = lazy(() =>
  import("@/pages/MLStudioArtifactsPage").then((module) => ({
    default: module.MLStudioArtifactsPage,
  })),
);
const MLStudioArtifactDetailPage = lazy(() =>
  import("@/pages/MLStudioArtifactDetailPage").then((module) => ({
    default: module.MLStudioArtifactDetailPage,
  })),
);
const MLStudioMetricsPage = lazy(() =>
  import("@/pages/MLStudioMetricsPage").then((module) => ({
    default: module.MLStudioMetricsPage,
  })),
);
const MLStudioPoliciesPage = lazy(() =>
  import("@/pages/MLStudioPoliciesPage").then((module) => ({
    default: module.MLStudioPoliciesPage,
  })),
);
const MLStudioPolicyEvalsPage = lazy(() =>
  import("@/pages/MLStudioPolicyEvalsPage").then((module) => ({
    default: module.MLStudioPolicyEvalsPage,
  })),
);
const MLStudioPolicyEvalsDetailPage = lazy(() =>
  import("@/pages/MLStudioPolicyEvalsDetailPage").then((module) => ({
    default: module.MLStudioPolicyEvalsDetailPage,
  })),
);
const MLStudioPolicyEvalCheckpointPage = lazy(() =>
  import("@/pages/MLStudioPolicyEvalCheckpointPage").then((module) => ({
    default: module.MLStudioPolicyEvalCheckpointPage,
  })),
);
const MLStudioPolicyDetailPage = lazy(() =>
  import("@/pages/MLStudioPolicyDetailPage").then((module) => ({
    default: module.MLStudioPolicyDetailPage,
  })),
);
const MLStudioPiPolicyDetailPage = lazy(() =>
  import("@/pages/MLStudioPiPolicyDetailPage").then((module) => ({
    default: module.MLStudioPiPolicyDetailPage,
  })),
);
const CollectorInsightsPage = lazy(() =>
  import("@/pages/CollectorInsightsPage").then((module) => ({
    default: module.CollectorInsightsPage,
  })),
);
const DashboardsPage = lazy(() =>
  import("@/pages/DashboardsPage").then((module) => ({ default: module.DashboardsPage })),
);
const DashboardDetailPage = lazy(() =>
  import("@/pages/DashboardDetailPage").then((module) => ({
    default: module.DashboardDetailPage,
  })),
);
const DashboardNewPage = lazy(() =>
  import("@/pages/DashboardNewPage").then((module) => ({ default: module.DashboardNewPage })),
);
const DashboardEditPage = lazy(() =>
  import("@/pages/DashboardEditPage").then((module) => ({ default: module.DashboardEditPage })),
);
const DevserversListPage = lazy(() =>
  import("@/pages/DevserversPage").then((module) => ({ default: module.DevserversListPage })),
);
const DevserversNewPage = lazy(() =>
  import("@/pages/DevserversNewPage").then((module) => ({ default: module.DevserversNewPage })),
);
const DevserverDetailPage = lazy(() =>
  import("@/pages/DevserverDetailPage").then((module) => ({
    default: module.DevserverDetailPage,
  })),
);
const TelemetryPage = lazy(() =>
  import("@/pages/TelemetryPage").then((module) => ({ default: module.TelemetryPage })),
);
const FleetSessionsPage = lazy(() =>
  import("@/pages/FleetSessionsPage").then((module) => ({
    default: module.FleetSessionsPage,
  })),
);
const FleetSessionDetailPage = lazy(() =>
  import("@/pages/FleetSessionDetailPage").then((module) => ({
    default: module.FleetSessionDetailPage,
  })),
);
const FleetRobotsPage = lazy(() =>
  import("@/pages/FleetRobotsPage").then((module) => ({
    default: module.FleetRobotsPage,
  })),
);
const FleetSitesPage = lazy(() =>
  import("@/pages/FleetSitesPage").then((module) => ({
    default: module.FleetSitesPage,
  })),
);
const FleetSiteDetailPage = lazy(() =>
  import("@/pages/FleetSiteDetailPage").then((module) => ({
    default: module.FleetSiteDetailPage,
  })),
);
const FleetRobotDetailPage = lazy(() =>
  import("@/pages/FleetRobotDetailPage").then((module) => ({
    default: module.FleetRobotDetailPage,
  })),
);
const SettingsPage = lazy(() =>
  import("@/pages/SettingsPage").then((module) => ({ default: module.SettingsPage })),
);
const DesignPage = lazy(() =>
  import("@/pages/DesignPage").then((module) => ({ default: module.DesignPage })),
);
const BrandPage = lazy(() =>
  import("@/pages/DesignPage/BrandPage").then((module) => ({ default: module.BrandPage })),
);
const SopsPage = lazy(() =>
  import("@/pages/SopsPage").then((module) => ({ default: module.SopsPage })),
);
const SopNewPage = lazy(() =>
  import("@/pages/SopNewPage").then((module) => ({ default: module.SopNewPage })),
);
const SopViewPage = lazy(() =>
  import("@/pages/SopViewPage").then((module) => ({
    default: module.SopViewPage,
  })),
);
const SopEditPage = lazy(() =>
  import("@/pages/SopEditPage").then((module) => ({
    default: module.SopEditPage,
  })),
);

type AppRoute = {
  path: string;
  element: ComponentProps<typeof Route>["element"];
};

type RouteGroup = {
  id: string;
  routes: AppRoute[];
};

function RouteLoader() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <Spinner size="lg" />
    </div>
  );
}

function withRouteLoader(node: ComponentProps<typeof Route>["element"]) {
  return <Suspense fallback={<RouteLoader />}>{node}</Suspense>;
}

/**
 * `/` is the Home product: it renders the assistant workspace. An external
 * organization has no Home tab, so it lands on the episode list instead. The
 * catch-all route also sends every unknown path here, so both land in one
 * place.
 *
 * Default open: while `/me` loads or after it fails, `homeLandingPath` returns
 * `/` and this renders Home, so an internal user sees no change.
 *
 * The redirect survives mutation untested, by decision. The webapp vitest
 * suite is node-environment with no `@testing-library/react` (root
 * `CLAUDE.md`), so this component cannot be rendered here. `homeLandingPath`
 * is pinned by `app/navigation.test.ts`, and the redirect itself is covered by
 * "the root path lands on the episode list" in `e2e/authz.spec.ts`, which runs
 * once the `unitzero.ai` user is provisioned on dev.
 */
function HomeRoute() {
  const me = useOrgRendering();
  const landingPath = homeLandingPath(me);
  if (landingPath !== "/") {
    return <Navigate to={landingPath} replace />;
  }
  return <DashboardPage />;
}

function LegacyWorkflowDetailRedirect() {
  const { id } = useParams();
  return <Navigate to={id ? `/ml-studio/workflows/${id}` : "/ml-studio/workflows"} replace />;
}

function LegacyTeleopSopRedirect() {
  const { id } = useParams();
  return <Navigate to={id ? `/sops/${id}` : "/sops"} replace />;
}

const routeGroups: RouteGroup[] = [
  {
    id: "home",
    routes: [
      { path: "/", element: <HomeRoute /> },
      { path: "/chat/:sessionId", element: withRouteLoader(<ChatPage />) },
    ],
  },
  {
    id: "data-studio",
    routes: [
      { path: "/episodes", element: withRouteLoader(<EpisodesPage />) },
      { path: "/episodes/:id", element: withRouteLoader(<EpisodeDetailPage />) },
      { path: "/review", element: withRouteLoader(<ReviewPage />) },
      { path: "/subtasks", element: withRouteLoader(<SubtasksPage />) },
      { path: "/datasets", element: withRouteLoader(<DatasetsPage />) },
      { path: "/datasets/new", element: withRouteLoader(<DatasetCuratePage />) },
      {
        path: "/datasets/:datasetId/episodes/:episodeId",
        element: withRouteLoader(<DatasetEpisodeDetailPage />),
      },
      {
        path: "/datasets/:datasetId/batch-label",
        element: withRouteLoader(<DatasetBatchLabelPage />),
      },
      { path: "/datasets/:id", element: withRouteLoader(<DatasetDetailPage />) },
      // Unified SOPs page and document/version routes.
      { path: "/sops", element: withRouteLoader(<SopsPage />) },
      { path: "/sops/new", element: withRouteLoader(<SopNewPage />) },
      { path: "/sops/:id", element: withRouteLoader(<SopViewPage />) },
      { path: "/sops/:id/edit", element: withRouteLoader(<SopEditPage />) },
      { path: "/sops/teleop/:id", element: <LegacyTeleopSopRedirect /> },
      {
        path: "/review/sops",
        element: <Navigate to="/sops" replace />,
      },
      { path: "/review/sops/:id", element: <Navigate to="/sops" replace /> },
    ],
  },
  {
    id: "analytics",
    routes: [
      { path: "/dashboards", element: withRouteLoader(<DashboardsPage />) },
      { path: "/dashboards/new", element: withRouteLoader(<DashboardNewPage />) },
      { path: "/dashboards/:id", element: withRouteLoader(<DashboardDetailPage />) },
      { path: "/dashboards/:id/edit", element: withRouteLoader(<DashboardEditPage />) },
      { path: "/collector-insights", element: withRouteLoader(<CollectorInsightsPage />) },
    ],
  },
  {
    id: "ml-studio",
    routes: [
      { path: "/ml-studio", element: <Navigate to="/ml-studio/workflows" replace /> },
      {
        path: "/ml-studio/experiments",
        element: <Navigate to="/ml-studio/workflows" replace />,
      },
      {
        path: "/ml-studio/experiments/new",
        element: <Navigate to="/ml-studio/workflows/new" replace />,
      },
      { path: "/ml-studio/experiments/:id", element: <LegacyWorkflowDetailRedirect /> },
      { path: "/ml-studio/workflows", element: withRouteLoader(<MLStudioWorkflowsPage />) },
      { path: "/ml-studio/workflows/new", element: withRouteLoader(<MLStudioNewWorkflowPage />) },
      {
        path: "/ml-studio/workflows/:id",
        element: withRouteLoader(<MLStudioWorkflowDetailPage />),
      },
      { path: "/ml-studio/artifacts", element: withRouteLoader(<MLStudioArtifactsPage />) },
      {
        path: "/ml-studio/artifacts/:id",
        element: withRouteLoader(<MLStudioArtifactDetailPage />),
      },
      { path: "/ml-studio/policies", element: withRouteLoader(<MLStudioPoliciesPage />) },
      { path: "/ml-studio/policy-evals", element: withRouteLoader(<MLStudioPolicyEvalsPage />) },
      {
        path: "/ml-studio/policy-evals/:baseSopId/checkpoints/:pinnedCheckpointId",
        element: withRouteLoader(<MLStudioPolicyEvalCheckpointPage />),
      },
      {
        path: "/ml-studio/policy-evals/:baseSopId",
        element: withRouteLoader(<MLStudioPolicyEvalsDetailPage />),
      },
      {
        path: "/ml-studio/policies/pi/:runId",
        element: withRouteLoader(<MLStudioPiPolicyDetailPage />),
      },
      {
        path: "/ml-studio/policies/:id",
        element: withRouteLoader(<MLStudioPolicyDetailPage />),
      },
      { path: "/ml-studio/metrics", element: withRouteLoader(<MLStudioMetricsPage />) },
      { path: "/devservers", element: withRouteLoader(<DevserversListPage />) },
      { path: "/devservers/new", element: withRouteLoader(<DevserversNewPage />) },
      { path: "/devservers/:id", element: withRouteLoader(<DevserverDetailPage />) },
    ],
  },
  {
    id: "fleet",
    routes: [
      { path: "/fleet", element: withRouteLoader(<FleetSitesPage />) },
      {
        path: "/fleet/sites/:slug",
        element: withRouteLoader(<FleetSiteDetailPage />),
      },
      { path: "/fleet/robots", element: withRouteLoader(<FleetRobotsPage />) },
      {
        path: "/fleet/robots/:host",
        element: withRouteLoader(<FleetRobotDetailPage />),
      },
      { path: "/telemetry", element: withRouteLoader(<TelemetryPage />) },
      {
        path: "/telemetry/sessions",
        element: withRouteLoader(<FleetSessionsPage />),
      },
      {
        path: "/telemetry/sessions/:id",
        element: withRouteLoader(<FleetSessionDetailPage />),
      },
    ],
  },
  {
    id: "settings",
    routes: [{ path: "/settings", element: withRouteLoader(<SettingsPage />) }],
  },
];

export function AuthenticatedRoutes() {
  // A tab the rail hides also stops answering deep links: its routes are not
  // registered, so the path falls through to the catch-all and lands on
  // `homeLandingPath`. Default open, the same rule the rail follows.
  const me = useOrgRendering();
  return (
    <Routes>
      {/* The OAuth consent card shows without the app chrome, like the login card. */}
      <Route path="/oauth/consent" element={withRouteLoader(<OAuthConsentPage />)} />
      <Route element={<AppLayout />}>
        {/* Register before the catch-all so /design never falls through to `*`. */}
        <Route path="/design" element={withRouteLoader(<DesignPage />)} />
        <Route path="/design/brand" element={withRouteLoader(<BrandPage />)} />
        {routeGroups
          .filter((group) => isRouteGroupVisible(group.id, me))
          .flatMap((group) =>
            group.routes.map((route) => (
              <Route key={`${group.id}:${route.path}`} path={route.path} element={route.element} />
            )),
          )}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
